import React, { useState, useCallback } from 'react';
import { delimited } from '../../../common/utils/commonUtil';
import IncomeModal from './incomeModal';
import AngelTaxResultView from './angelTaxResultView';
import NewsPicksBanner, {
  type NewsPicksBannerProps,
} from '../common/newsPicksBanner';
import {
  calculateAngelTax,
  type AngelTax,
} from '../../models/questions/angelTaxCalculator';

interface Props {
  taxReturnImage: string;
  taxWithholdingImage: string;
  newsPicksBanner: NewsPicksBannerProps | null;
}

const numberWithComma = (value: string): string => {
  if (!/^[0-9]+$/.test(value)) {
    return value;
  }
  return delimited(Math.floor(parseInt(value)));
};

const deleteComma = (value: string): string => value.replace(/,/g, '');

const integerWithoutComma = (value: string): number => {
  if (value === '') return 0;
  return parseInt(deleteComma(value));
};

export default function AngelTaxView({
  taxReturnImage,
  taxWithholdingImage,
  newsPicksBanner,
}: Props) {
  const [income, setIncome] = useState('');
  const [incomeError, setIncomeError] = useState<string | null>(null);
  const [investment, setInvestment] = useState('');
  const [investmentError, setInvestmentError] = useState<string | null>(null);
  const [gain, setGain] = useState('0');
  const [gainError, setGainError] = useState<string | null>(null);
  const [gainByOutsideStock, setGainByOutsideStock] = useState('0');
  const [gainByOutsideStockError, setGainByOutsideStockError] = useState<
    string | null
  >(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [incomeTax, setIncomeTax] = useState<number | null>(null);
  const [angelTaxA, setAngelTaxA] = useState<AngelTax | null>(null);
  const [angelTaxB, setAngelTaxB] = useState<AngelTax | null>(null);

  const errorMessageOnChange = useCallback((value?: string): string | null => {
    if (value == null || value === '') {
      return null;
    }
    if (!/^[0-9]+$/.test(value)) {
      return '半角数字を入力してください';
    }
    if (parseInt(value) >= parseInt('1000000000000')) {
      return '1,000,000,000,000未満の値を入力してください';
    }
    return null;
  }, []);

  const handleSubmit = () => {
    setShowResult(true);
    if (
      income === '' ||
      incomeError != null ||
      gainError != null ||
      gainByOutsideStockError != null
    ) {
      setIncomeTax(null);
      setAngelTaxA(null);
      setAngelTaxB(null);
      return;
    }
    // investment, gain, gainByOutsideStockは空でもOK（空は0として扱う）。
    // 初期値に0を設定しないようにしたため。
    const result = calculateAngelTax({
      income: integerWithoutComma(income),
      investment: integerWithoutComma(investment),
      gain: integerWithoutComma(gain),
      gainByOutsideStock: integerWithoutComma(gainByOutsideStock),
    });
    setIncomeTax(result.incomeTax);
    // 投資額が不正なら A/B は表示しない
    const hasValidInvestment = investmentError == null;
    setAngelTaxA(hasValidInvestment ? result.angelTaxA : null);
    setAngelTaxB(hasValidInvestment ? result.angelTaxB : null);
  };

  return (
    <div className="angel-main-wrap">
      {newsPicksBanner && (
        <NewsPicksBanner
          banner={newsPicksBanner}
          className="angel-news-picks"
        />
      )}
      <h1 className="angel-title">エンジェル税制シミュレーター</h1>
      <div className="alert alert-primary alert-dismissible angel-alert-text">
        <b>所得税の計算について</b>
        <ul>
          <li>
            令和7年12月時点の税制を基にした概算のシミュレーションとなります。
          </li>
          <li>
            所得控除は基礎控除のみで、その他控除については考慮していません。
          </li>
          <li>所得税額には、復興特別所得税額(2.1%)が含まれています。</li>
          <li>
            株式譲渡は分離課税選択で税率は15.315%(所得税および復興特別所得税)です。
          </li>
          <li>
            基礎控除の計算については、総所得金額と合計所得金額が同一である前提で行っています。
          </li>
        </ul>
      </div>
      <div>
        <form className="top-form">
          <div
            className={
              incomeError != null
                ? 'angel-form-input-block form-group income has-error'
                : 'angel-form-input-block form-group income'
            }
          >
            <label
              className="control-label sp-none required-label income-label"
              htmlFor="income"
            >
              総所得金額
              <div
                className="question-icon"
                onClick={() => setShowIncomeModal(true)}
              ></div>
            </label>

            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="income"
                  type="text"
                  inputMode="numeric"
                  className={
                    income !== '' && incomeError === null
                      ? 'form-control required is-valid'
                      : 'form-control required'
                  }
                  value={income}
                  onChange={e => {
                    setIncome(e.target.value);
                    setIncomeError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setIncome(value);
                    setIncomeError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setIncome(numberWithComma(e.target.value));
                    setIncomeError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 10000000"
                />
                <span>円</span>
              </div>
              {incomeError && (
                <span className="help-block error-message">{incomeError}</span>
              )}
            </div>
          </div>
          <div
            className={
              investmentError != null
                ? 'angel-form-input-block form-group investment has-error'
                : 'angel-form-input-block form-group investment'
            }
          >
            <label
              className="control-label required-label"
              htmlFor="investment"
            >
              エンジェル税制適用会社への投資額
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="investment"
                  type="text"
                  inputMode="numeric"
                  className={
                    investment !== '' && investmentError === null
                      ? 'form-control required is-valid'
                      : 'form-control required'
                  }
                  value={investment}
                  onChange={e => {
                    setInvestment(e.target.value);
                    setInvestmentError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setInvestment(value);
                    setInvestmentError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setInvestment(numberWithComma(e.target.value));
                    setInvestmentError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 500000"
                />
                <span>円</span>
              </div>
              {investmentError && (
                <span className="help-block error-message">
                  {investmentError}
                </span>
              )}
            </div>
          </div>
          <div
            className={
              gainError != null
                ? 'angel-form-input-block form-group gain has-error'
                : 'angel-form-input-block form-group gain'
            }
          >
            <label className="control-label optional-label" htmlFor="gain">
              上場株式の売買による譲渡益
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="gain"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={gain}
                  onChange={e => {
                    setGain(e.target.value);
                    setGainError(errorMessageOnChange(e.target.value));
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setGain(value);
                    setGainError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setGain(numberWithComma(e.target.value));
                    setGainError(errorMessageOnChange(e.target.value));
                  }}
                  placeholder="例, 1000000"
                />
                <span>円</span>
              </div>
              {gainError && (
                <span className="help-block error-message">{gainError}</span>
              )}
              <p className="help-block pt-2 font-weight-normal">
                ※上場株式の売買において譲渡損の場合は0を記入してください。
                <br />
                ※上場株式に関わる譲渡損失の繰越がない前提で記入してください。
                <br />
                ※株式譲渡は特定口座（源泉あり）で取引されていることが前提です。
              </p>
            </div>
          </div>
          <div
            className={
              gainByOutsideStockError != null
                ? 'angel-form-input-block form-group gain-by-outside-stock has-error'
                : 'angel-form-input-block form-group gain-by-outside-stock'
            }
          >
            <label
              className="control-label optional-label"
              htmlFor="gain-by-outside-stock"
            >
              非上場株式の売買による譲渡益
            </label>
            <div className="angel-form-input-wrap">
              <div className="angel-form-input-inr">
                <input
                  id="gain-by-outside-stock"
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  value={gainByOutsideStock}
                  onChange={e => {
                    setGainByOutsideStock(e.target.value);
                    setGainByOutsideStockError(
                      errorMessageOnChange(e.target.value),
                    );
                  }}
                  onFocus={e => {
                    const value = deleteComma(e.target.value);
                    setGainByOutsideStock(value);
                    setGainByOutsideStockError(errorMessageOnChange(value));
                  }}
                  onBlur={e => {
                    setGainByOutsideStock(numberWithComma(e.target.value));
                    setGainByOutsideStockError(
                      errorMessageOnChange(e.target.value),
                    );
                  }}
                  placeholder="例, 1000000"
                />
                <span>円</span>
              </div>
              {gainByOutsideStockError && (
                <span className="help-block error-message">
                  {gainByOutsideStockError}
                </span>
              )}
              <p className="help-block pt-2 font-weight-normal">
                ※非上場株式の売買において譲渡損の場合は0を記入してください。
                <br />
                ※非上場株式に関わる譲渡損失の繰越がない前提で記入してください。
              </p>
            </div>
          </div>
        </form>

        <div
          className={
            showResult ? 'calculation-submit recalculate' : 'calculation-submit'
          }
          onClick={() => handleSubmit()}
        >
          <a>{showResult ? '再計算する' : '計算する'}</a>
        </div>
      </div>

      <AngelTaxResultView
        showResult={showResult}
        incomeTax={incomeTax}
        angelTaxA={angelTaxA}
        angelTaxB={angelTaxB}
      />

      <IncomeModal
        show={showIncomeModal}
        taxReturnImage={taxReturnImage}
        taxWithholdingImage={taxWithholdingImage}
        onClose={() => setShowIncomeModal(false)}
      />
      <div className="angel-tax__cta">
        <div className="angel-tax__cta__inner">
          <div className="angel-tax__cta__text">
            最短３分！WEBで完結！
            <br />
            登録後、新しい案件情報をお届けします。
          </div>
          <a href="/users/sign_up" className="angel-tax__cta__btn">
            無料で投資家登録
          </a>
        </div>
      </div>
    </div>
  );
}
